import { DataType, Form, FormOptions, Section } from '@globaleris/ui.library'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumSubtipoCiclo } from '../../../../api/oData/enums/enumSubtipoCiclo'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { enumToSelectOptionArray } from '../../../../helpers/enumHelper'
import { getCustomerDomain } from '../../../../redux'
import { RegenerateFormData } from '../../settlements.models'

interface RegenerateExecutionCycleDialogProps {
  open: boolean
  onClose: (data?: RegenerateFormData) => void
  initialData?: RegenerateFormData
}

const initialFormData: RegenerateFormData = {
  startDate: undefined,
  endDate: undefined,
  invoiceCycleSubtype: undefined
}

export const RegenerateExecutionCycleDialog: React.FC<RegenerateExecutionCycleDialogProps> = ({
  open,
  onClose,
  initialData
}) => {
  const [t] = useTranslation(['settlementsInvoices', 'enums', 'globalerisLibrary'])
  const [formData, setFormData] = useState<RegenerateFormData>(initialFormData)
  const [formValid, setFormValid] = useState(false)

  const sections: Section[] = useMemo(
    () => [
      {
        inputs: [
          {
            name: 'startDate',
            label: t('settlementsInvoices:components.regenerateDialog.startDate'),
            type: DataType.DATE,
            inputsize: 6,
            required: true,
            onChange: (value: any) => {
              setFormData(prev => ({
                ...prev,
                startDate: value != null ? value.toString() : undefined
              }))
            }
          },
          {
            name: 'endDate',
            label: t('settlementsInvoices:components.regenerateDialog.endDate'),
            type: DataType.DATE,
            inputsize: 6,
            required: true,
            onChange: (value: any) => {
              setFormData(prev => ({
                ...prev,
                endDate: value != null ? value.toString() : undefined
              }))
            }
          },
          {
            name: 'invoiceCycleSubtype',
            label: t('settlementsInvoices:components.regenerateDialog.subtype'),
            type: DataType.SELECT,
            inputsize: 12,
            required: false,
            selectOptions: enumToSelectOptionArray(EnumSubtipoCiclo, 'enums:EnumSubtipoCiclo'),
            onChange: (value: any) => {
              setFormData(prev => ({
                ...prev,
                invoiceCycleSubtype: value ? +value : undefined
              }))
            }
          }
        ]
      }
    ],
    [t]
  )

  const formOptions: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 2,
      analytics: {
        customer: getCustomerDomain(),
        view: 'settlementsInvoices:regenerateExecutionCycleDialog'
      }
    }),
    []
  )

  useEffect(() => {
    setFormValid(!!formData.startDate && !!formData.endDate)
  }, [formData])

  useEffect(() => {
    if (open) {
      setFormData(initialData ?? initialFormData)
      setFormValid(false)
    }
  }, [open])

  const footerActions: CardActionProps[] = useMemo(
    () => [
      {
        label: t('globalerisLibrary:form.actions.cancel'),
        color: 'secondary',
        handleActionClick: () => onClose(undefined)
      },
      {
        label: t('settlementsInvoices:components.regenerateDialog.confirm'),
        color: 'primary',
        disabled: !formValid,
        handleActionClick: () => onClose(formData)
      }
    ],
    [t, formValid, formData, onClose]
  )

  return (
    <SimpleDialog
      open={open}
      handleClose={() => onClose(undefined)}
      footerActions={footerActions}
      maxWidth="md"
      formStyle="formDialog"
    >
      <Form
        sections={sections}
        data={formData}
        options={formOptions}
        title={t('settlementsInvoices:components.regenerateDialog.title')}
      />
    </SimpleDialog>
  )
}
